// ═══════════════════════════════════════════════════════════════════════════
// Mis ventas — una lista con lentes, no tres pantallas
//
// EL DEFECTO QUE ESTO CORRIGE, dicho por el operador: "Cuentas, Historial y Mi
// cuenta proporcionan información redundante... Lo que le importa al vendedor
// es cuáles ya se pagaron, cuáles están pendientes de pago y sobre todo cuándo
// va a recibir su comisión."
//
// Y era peor que redundante: NINGUNA pantalla mostraba los tres estados a la
// vez. Se midió, y el reparto era este —
//
//              timbrada   pagada   comisión
//   Historial     sí        NO        sí
//   Cuentas       sí        sí        NO
//   Mi cuenta     NO        NO        sí
//
// — así que "¿esta remisión ya se timbró, ya se pagó, y cuándo cobro?" no la
// contestaba ninguna: había que visitar las tres y casar folios a ojo.
//
// LA IDEA: la unidad es la REMISIÓN, y las tres pantallas eran la misma lista
// con distinta pregunta encima. Aquí vive esa lista: cada renglón con sus tres
// estados juntos, y la manera de agrupar y filtrar. Sin nada de React, para
// que se pueda probar de verdad.
//
// ESTO ES PARA EL VENDEDOR. Cuentas e Historial se quedan como están para el
// dueño y la gerencia — decisión del operador: "el dueño o administrador pueden
// ver eso; la vista fusionada es para los vendedores". Quien administra necesita
// cruzar clientes y perseguir cobranza; quien vende necesita su lista.
// ═══════════════════════════════════════════════════════════════════════════

import { estadoCobranza, saldoPendiente, saldoAFavor, totalPagado,
         estadoFacturacion, remisionCancelada, requiereREP } from "./cobranza.js";
import { estadoComision, COMISION } from "./estado-comision.js";

/**
 * Los tres estados de una remisión, en un solo objeto.
 *
 * Se calculan aquí y no en la pantalla para que digan lo mismo en todos lados:
 * el defecto que veníamos arrastrando era justamente que cada pantalla
 * calculaba lo suyo y ninguna las tres.
 */
export function estadosDe(inv) {
  return {
    fiscal:   estadoFacturacion(inv),
    cobranza: estadoCobranza(inv),
    comision: estadoComision(inv),
    saldo:    saldoPendiente(inv),
    // Lo que sobró. Va aparte del saldo porque se persigue distinto: uno se
    // cobra y el otro se devuelve o se aplica.
    aFavor:   saldoAFavor(inv),
    pagado:   totalPagado(inv),
    cancelada: remisionCancelada(inv),
  };
}

/**
 * Los tres números de arriba: la respuesta corta.
 *
 * `porCobrar` es del CLIENTE (lo que debe), `porAcreditarse` es del VENDEDOR
 * (lo que le deben a él). Son cosas distintas y confundirlas es lo que produce
 * el reclamo de "vendí y no me han pagado" cuando quien no ha pagado es el
 * cliente.
 */
export function resumenVentas(invoices = []) {
  let porCobrar = 0, porFacturar = 0, comisionPorAcreditarse = 0, comisionAcreditada = 0;
  for (const inv of invoices) {
    const e = estadosDe(inv);
    if (e.cancelada) continue;
    porCobrar += e.saldo;
    if (e.fiscal.clave === "por_facturar") porFacturar += 1;
    if (e.comision.clave === COMISION.por_acreditarse.clave)
      comisionPorAcreditarse += Number(inv.comision) || 0;
    if (e.comision.clave === COMISION.acreditada.clave)
      comisionAcreditada += Number(inv.comision) || 0;
  }
  const r = (n) => Math.round(n * 100) / 100;
  return {
    porCobrar: r(porCobrar),
    porFacturar,
    comisionPorAcreditarse: r(comisionPorAcreditarse),
    comisionAcreditada: r(comisionAcreditada),
  };
}

/** Los filtros que ofrece la vista, con las palabras que se usan al hablar. */
export const LENTES = {
  todas:          { clave: "todas",          etiqueta: "Todas" },
  por_cobrar:     { clave: "por_cobrar",     etiqueta: "Me deben" },
  pagadas:        { clave: "pagadas",        etiqueta: "Ya pagadas" },
  por_facturar:   { clave: "por_facturar",   etiqueta: "Falta timbrar" },
  facturadas:     { clave: "facturadas",     etiqueta: "Facturadas" },
  falta_rep:      { clave: "falta_rep",      etiqueta: "Falta complemento" },
  a_favor:        { clave: "a_favor",        etiqueta: "Con saldo a favor" },
  por_acreditarse:{ clave: "por_acreditarse",etiqueta: "Mi comisión, por acreditarse" },
  canceladas:     { clave: "canceladas",     etiqueta: "Canceladas" },
};

/**
 * Filtra por lente. Las canceladas se ESCONDEN salvo que se pidan: son
 * documentos muertos y en la lista diaria solo estorban — pero no se borran ni
 * se ocultan del todo, que es la regla de la casa.
 */
export function filtrarPorLente(invoices = [], lente = "todas") {
  return invoices.filter(inv => {
    const e = estadosDe(inv);
    if (lente === "canceladas") return e.cancelada;
    if (e.cancelada) return false;
    switch (lente) {
      case "por_cobrar":      return e.saldo > 0;
      case "pagadas":         return e.cobranza.clave === "pagada";
      case "por_facturar":    return e.fiscal.clave === "por_facturar";
      case "facturadas":      return e.fiscal.clave === "facturada";
      // Las que deben un REP: es lo que el SAT reclama y lo que más urge ver.
      case "falta_rep":       return requiereREP(inv);
      // Para conciliar: lo que hay que devolver o aplicar a otra remisión.
      case "a_favor":         return e.aFavor > 0;
      case "por_acreditarse": return e.comision.clave === COMISION.por_acreditarse.clave;
      default:                return true;
    }
  });
}

/** Busca por folio o por nombre de cliente. Sin acentos y sin mayúsculas. */
export function buscar(invoices = [], texto = "") {
  const q = normalizar(texto);
  if (!q) return invoices;
  return invoices.filter(inv =>
    String(inv.folio ?? "").includes(q) ||
    normalizar(inv.client?.name).includes(q));
}

const normalizar = (s) => String(s ?? "")
  .toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();

/**
 * Agrupa. `fecha` es la lista de siempre —de la más reciente a la más vieja,
 * partida por mes— y `cliente` es lo que hacía Cuentas.
 *
 * Devuelve [{ clave, titulo, invoices, total, saldo }], ya ordenado.
 */
export function agrupar(invoices = [], por = "fecha") {
  const grupos = new Map();

  for (const inv of invoices) {
    const { clave, titulo, orden } = etiquetaDeGrupo(inv, por);
    if (!grupos.has(clave)) grupos.set(clave, { clave, titulo, orden, invoices: [] });
    grupos.get(clave).invoices.push(inv);
  }

  const lista = [...grupos.values()].map(g => {
    // Dentro de cada grupo, siempre de la más reciente a la más vieja: es como
    // se busca un documento cuando se tiene la fecha en la cabeza.
    g.invoices.sort((a, b) => new Date(b.date) - new Date(a.date));
    let total = 0, saldo = 0;
    for (const inv of g.invoices) {
      if (remisionCancelada(inv)) continue;
      total += Number(inv.total) || 0;
      saldo += saldoPendiente(inv);
    }
    return { ...g, total: Math.round(total * 100) / 100,
                   saldo: Math.round(saldo * 100) / 100 };
  });

  // Cada agrupado se ordena por lo que le importa:
  //
  //   fecha   → del mes más reciente al más viejo
  //   estado  → por la PRIORIDAD declarada en `etiquetaDeGrupo`, no por saldo.
  //             Lo que el SAT reclama va arriba aunque sean tres pesos; el
  //             saldo grande es de la vinícola, el complemento que falta tiene
  //             consecuencias fuera de ella.
  //   cliente → quien más debe primero, que es a quien hay que llamar
  //
  // Se ordenaba todo por saldo, así que la prioridad de `estado` estaba
  // escrita y no se aplicaba. Lo encontró la prueba.
  lista.sort((a, b) => {
    if (por === "fecha")  return String(b.orden).localeCompare(String(a.orden));
    if (por === "estado") return String(a.orden).localeCompare(String(b.orden));
    return (b.saldo - a.saldo) || (b.total - a.total);
  });
  return lista;
}

function etiquetaDeGrupo(inv, por) {
  if (por === "cliente") {
    const n = inv.client?.name || "Sin cliente";
    return { clave: inv.client?.id || n, titulo: n, orden: n };
  }
  if (por === "estado") {
    const e = estadosDe(inv);
    if (e.cancelada) return { clave: "cancelada", titulo: "Canceladas", orden: "9" };
    // Lo que el SAT reclama va PRIMERO: una factura sin su complemento es lo
    // único de esta lista que tiene consecuencias fuera de la vinícola.
    if (requiereREP(inv))
      return { clave: "falta_rep", titulo: "Falta el complemento de pago", orden: "1" };
    if (e.fiscal.clave === "por_facturar")
      return { clave: "por_facturar", titulo: "Falta timbrar", orden: "2" };
    if (e.saldo > 0) return { clave: "debe", titulo: "Me deben", orden: "3" };
    if (e.aFavor > 0)
      return { clave: "a_favor", titulo: "Con saldo a favor del cliente", orden: "35" };
    if (e.fiscal.clave === "facturada")
      return { clave: "facturada", titulo: "Facturadas y cobradas", orden: "4" };
    return { clave: "cerrada", titulo: "Cerradas", orden: "5" };
  }
  // Por fecha: un grupo por mes. Sin `toLocaleDateString` con opciones, que
  // depende del entorno y haría las pruebas frágiles.
  // Se comprueba la AUSENCIA antes de construir la fecha: `new Date(null)` no
  // da NaN, da el 1 de enero de 1970. Una remisión sin fecha se habría ido
  // calladita a un grupo "enero 1970" al final de la lista, que es peor que
  // decir que no la tiene.
  if (inv.date == null || inv.date === "")
    return { clave: "sin-fecha", titulo: "Sin fecha", orden: "0000-00" };
  const d = new Date(inv.date);
  if (isNaN(d)) return { clave: "sin-fecha", titulo: "Sin fecha", orden: "0000-00" };
  const clave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  return { clave, titulo: `${MESES[d.getMonth()]} ${d.getFullYear()}`, orden: clave };
}

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio",
               "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
